import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Shield, FileText, Activity, Link, Monitor, UserCheck, UserX, Crown, Clock } from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  admins: number;
  activeUsers: number;
  pendingUsers: number;
  totalGroups: number;
  totalTemplates: number;
  globalTemplates: number;
}

interface AdminDashboardProps {
  stats: DashboardStats;
  onNavigate: (section: string) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ stats, onNavigate }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <p className="text-muted-foreground text-sm">Overview of your system</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Total Users"
          value={stats.totalUsers}
          onClick={() => onNavigate('users')}
        />
        <StatCard
          icon={<UserCheck className="w-5 h-5" />}
          label="Active"
          value={stats.activeUsers}
          variant="success"
          onClick={() => onNavigate('users')}
        />
        <StatCard
          icon={<UserX className="w-5 h-5" />}
          label="Pending / Suspended"
          value={stats.pendingUsers}
          variant={stats.pendingUsers > 0 ? 'warning' : 'default'}
          onClick={() => onNavigate('users')}
        />
        <StatCard
          icon={<Crown className="w-5 h-5" />}
          label="Administrators"
          value={stats.admins}
          onClick={() => onNavigate('users')}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={<Shield className="w-5 h-5" />}
          label="Groups"
          value={stats.totalGroups}
          onClick={() => onNavigate('groups')}
        />
        <StatCard
          icon={<FileText className="w-5 h-5" />}
          label="Templates"
          value={stats.totalTemplates}
          subtitle={`${stats.globalTemplates} global`}
          onClick={() => onNavigate('templates')}
        />
      </div>

      {/* Quick actions */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction icon={<Users className="w-5 h-5" />} label="Manage Users" onClick={() => onNavigate('users')} />
          <QuickAction icon={<Shield className="w-5 h-5" />} label="Manage Groups" onClick={() => onNavigate('groups')} />
          <QuickAction icon={<Link className="w-5 h-5" />} label="Onboarding" onClick={() => onNavigate('onboarding')} />
          <QuickAction icon={<Activity className="w-5 h-5" />} label="Monitoring" onClick={() => onNavigate('monitoring')} />
          <QuickAction icon={<Monitor className="w-5 h-5" />} label="Kiosk Settings" onClick={() => onNavigate('kiosk')} />
          <QuickAction icon={<FileText className="w-5 h-5" />} label="Templates" onClick={() => onNavigate('templates')} />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  subtitle?: string;
  variant?: 'default' | 'success' | 'warning';
  onClick?: () => void;
}> = ({ icon, label, value, subtitle, variant = 'default', onClick }) => (
  <Card
    className={`cursor-pointer hover:border-primary/50 transition-colors ${onClick ? '' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-muted-foreground">{icon}</span>
        {variant === 'success' && <Badge variant="secondary" className="bg-success/10 text-success text-[10px]">Active</Badge>}
        {variant === 'warning' && value > 0 && <Badge variant="secondary" className="bg-warning/10 text-warning text-[10px]">Attention</Badge>}
      </div>
      <div className={`text-3xl font-bold ${variant === 'success' ? 'text-success' : variant === 'warning' && value > 0 ? 'text-warning' : ''}`}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
    </CardContent>
  </Card>
);

const QuickAction: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 hover:border-primary/30 transition-colors text-left"
  >
    <span className="text-primary">{icon}</span>
    <span className="text-sm font-medium">{label}</span>
  </button>
);
